import { useQuery, useMutation, useQueryClient } from '../lib/queryClient';
import { getFilesList } from '../endpoints/files/list_GET.schema';
import { createFile } from '../endpoints/files/create_POST.schema';
import { updateFile } from '../endpoints/files/update_POST.schema';
import { deleteFile } from '../endpoints/files/delete_POST.schema';

function normalizeDirectoryPath(filePath?: string | null) {
  if (!filePath || filePath === '/') return '/';
  return filePath.endsWith('/') ? filePath.slice(0, -1) : filePath;
}

export const useSessionFiles = (sessionId: string | undefined, isAiActive: boolean = false) => {
  return useQuery({
    queryKey: ['files', 'list', sessionId],
    queryFn: () => {
      if (!sessionId) throw new Error('Session ID is required');
      return getFilesList({ sessionId });
    },
    enabled: !!sessionId,
    retry: false,
    // IMPORTANT: Disable refetchOnWindowFocus to prevent overwriting local changes on mobile
    // when switching apps/tabs. The database is the source of truth during editing.
    refetchOnWindowFocus: false,
    staleTime: isAiActive ? 1000 : 30000, // Poll faster when AI is working
    // Poll every 2 seconds when AI is active so users see file changes in real-time
    // Otherwise disable polling to prevent race conditions with local changes
    refetchInterval: isAiActive ? 2000 : false,
  });
};

export const useCreateFile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createFile,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['files', 'list', data.file.sessionId] });
    },
  });
};

export const useUpdateFile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateFile,
    onSuccess: (data) => {
      queryClient.setQueryData(
        ['files', 'list', data.file.sessionId],
        (cached: { files?: Array<Record<string, unknown>> } | undefined) => {
          if (!cached?.files) return cached;

          const nextFiles = cached.files.map((file) => {
            if (file.id !== data.file.id) return file;
            return {
              ...file,
              fileName: data.file.fileName,
              filePath: data.file.filePath,
              language: data.file.language,
              updatedAt: data.file.updatedAt,
            };
          });

          nextFiles.sort((left, right) => {
            const leftPath = `${normalizeDirectoryPath(String(left.filePath ?? '/'))}/${String(left.fileName ?? '')}`;
            const rightPath = `${normalizeDirectoryPath(String(right.filePath ?? '/'))}/${String(right.fileName ?? '')}`;
            return leftPath.localeCompare(rightPath);
          });

          return {
            ...cached,
            files: nextFiles,
          };
        },
      );
    },
  });
};

export const useDeleteFile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteFile,
    onSuccess: () => {
      // We don't have the sessionId in the response for delete, so we invalidate all file lists or we need to pass sessionId in variables if we want to be specific.
      // However, react-query's invalidateQueries matches loosely by prefix.
      // Ideally we should pass sessionId to the mutation context or variables to be more precise, but invalidating "files" is safe enough.
      queryClient.invalidateQueries({ queryKey: ['files'] });
    },
  });
};

export const useCleanupDuplicates = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (sessionId: string) => {
      const response = await fetch('/_api/files/cleanup-duplicates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId }),
      });
      if (!response.ok) {
        throw new Error('Failed to cleanup duplicates');
      }
      const { json } = await import('superjson');
      return json.parse(await response.text()) as { removed: number; remaining: number };
    },
    onSuccess: (data, sessionId) => {
      queryClient.invalidateQueries({ queryKey: ['files', 'list', sessionId] });
    },
  });
};
